import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { UserLookup } from "@/hooks/use-users";

interface Props {
  /** Hvem som har likt. Ukjente id-er (slettet bruker) hoppes over. */
  likedByUserIds: string[];
  likeCount: number;
  userHasLiked: boolean;
  /** Samme liste som CommentMessage bruker til @-mentions. */
  users: UserLookup[];
  authUserId: string | undefined;
  disabled: boolean;
  onToggle: () => void;
}

/**
 * VIB-2519 (William 09.09): hjertet viste bare et antall, så det var umulig å
 * se HVEM som hadde reagert — og dermed å tolke kommunikasjonen. Navnene slås
 * opp mot `users` og vises i en tooltip på hover/fokus, med de samme navnene
 * som `aria-label` slik at skjermleser og langtrykk får samme informasjon
 * (Radix-tooltips åpner ikke på tap).
 *
 * Skilt ut av CommentMessage.tsx for å holde den under 300 linjer.
 */
export function CommentLikeButton({
  likedByUserIds, likeCount, userHasLiked, users, authUserId, disabled, onToggle,
}: Props) {
  // Deg selv først som «Deg», resten alfabetisk. Ukjente id-er utelates helt
  // — en rå uuid i tooltipen er verre enn å hoppe over den.
  const others = likedByUserIds
    .filter((id) => id !== authUserId)
    .map((id) => users.find((u) => u.id === id)?.full_name)
    .filter((n): n is string => !!n)
    .sort((a, b) => a.localeCompare(b, "nb"));
  const names = likedByUserIds.includes(authUserId ?? "") ? ["Deg", ...others] : others;

  const button = (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1 hover:text-primary disabled:opacity-50",
        userHasLiked && "text-primary font-medium",
      )}
      aria-pressed={userHasLiked}
      aria-label={names.length > 0 ? `Likt av ${names.join(", ")}` : undefined}
    >
      <span aria-hidden>{userHasLiked ? "♥" : "♡"}</span>
      {likeCount > 0 ? likeCount : "Lik"}
    </button>
  );

  // Ingen reaksjoner (eller ingen navn å vise) → ingen tooltip. Et tomt
  // tooltip-skall er verre enn ingen.
  if (likeCount === 0 || names.length === 0) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent className="text-xs">
        {names.map((n) => (
          <div key={n}>{n}</div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
